/**
 * Smart Locks — landlord console for provisioning smart locks, issuing
 * time-bound PIN codes, and auditing access events. Pairs with
 * smart_keys_service backend.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { KeyRound, Plus, Activity, Ban, Lock, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const PROVIDERS = [
  { value: "stub", label: "Stub (demo / pre-hardware)" },
  { value: "remotelock", label: "RemoteLock" },
  { value: "august", label: "August" },
  { value: "yale", label: "Yale Access" },
  { value: "switchbot", label: "SwitchBot" },
];
const ROLES = ["tenant", "applicant", "vendor", "other"];
const STATUS_TONE = { active: "green", revoked: "red", expired: "slate", exhausted: "slate" };

export default function SmartLocks() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const [locks, setLocks] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProvision, setOpenProvision] = useState(false);
  const [openIssue, setOpenIssue] = useState(null); // lock
  const [openAudit, setOpenAudit] = useState(null); // {lock, items}
  const [codesByLock, setCodesByLock] = useState({});
  const [pform, setPform] = useState({ listing_id: "", nickname: "", provider: "stub", provider_device_id: "" });
  const [iform, setIform] = useState({ granted_to_name: "", granted_to_email: "", role: "applicant", valid_from: "", valid_to: "", max_uses: 20 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [lr, listr] = await Promise.all([
        fetch(`${API}/api/smart-locks`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
        fetch(`${API}/api/listings?limit=100`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
      ]);
      const myLocks = lr.items || [];
      setLocks(myLocks);
      setListings((listr.items || []).filter(l => l.landlord_id === uid));
      // Pull codes for each lock in parallel.
      const codeMap = {};
      await Promise.all(myLocks.map(async (l) => {
        const r = await fetch(`${API}/api/smart-locks/${l.id}/codes`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
        const d = await r.json();
        codeMap[l.id] = d.items || [];
      }));
      setCodesByLock(codeMap);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  async function provision(e) {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/api/smart-locks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify(pform),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not provision");
      toast.success("Lock provisioned");
      setOpenProvision(false);
      setPform({ listing_id: "", nickname: "", provider: "stub", provider_device_id: "" });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function issueCode(e) {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/api/smart-locks/${openIssue.id}/codes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify({
          ...iform,
          valid_from: iform.valid_from ? new Date(iform.valid_from).toISOString() : null,
          valid_to: iform.valid_to ? new Date(iform.valid_to).toISOString() : null,
          max_uses: Number(iform.max_uses) || 20,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not issue");
      toast.success(`PIN issued: ${d.code}`);
      setOpenIssue(null);
      setIform({ granted_to_name: "", granted_to_email: "", role: "applicant", valid_from: "", valid_to: "", max_uses: 20 });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function revoke(c) {
    const ok = await confirmDialog({
      title: `Revoke PIN for ${c.granted_to_name}?`,
      description: "This code will stop working immediately. The recipient will no longer be able to unlock the door.",
      confirmLabel: "Yes, revoke",
      cancelLabel: "Keep active",
      destructive: true,
    });
    if (!ok) return;
    await fetch(`${API}/api/smart-locks/codes/${c.id}/revoke`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
    load();
  }

  async function openLockAudit(lock) {
    const r = await fetch(`${API}/api/smart-locks/${lock.id}/audit`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
    const d = await r.json();
    setOpenAudit({ lock, items: d.items || [] });
  }

  async function copyCode(code) {
    await navigator.clipboard.writeText(code);
    toast.success("PIN copied");
  }

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Smart locks"
        subtitle="Provision smart locks per listing and issue time-bound PIN codes to tenants, applicants, and vendors."
        actions={<Button onClick={() => setOpenProvision(true)} disabled={listings.length === 0} data-testid="provision-lock"><Plus className="w-4 h-4"/> Provision lock</Button>}
      />

      {listings.length === 0 && (
        <Card className="p-4 mb-4 bg-amber-50 border-amber-200 text-sm text-amber-900">
          You need at least one listing before provisioning a smart lock.
        </Card>
      )}

      {locks.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No smart locks yet"
          description="Provision a lock for a listing and start issuing PIN codes that work without you ever needing to be present."
        />
      ) : (
        <div className="space-y-4">
          {locks.map(lock => {
            const codes = codesByLock[lock.id] || [];
            const active = codes.filter(c => c.status === "active");
            return (
              <Card key={lock.id} className="p-5" data-testid={`lock-${lock.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-slate-900 inline-flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-700"/> {lock.nickname}
                      <Pill tone={lock.provider === "stub" ? "slate" : "green"}>{lock.provider}</Pill>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{lock.listing?.title || ""} · {lock.listing?.city || ""}</div>
                    <div className="text-xs text-slate-500">{active.length} active PIN{active.length === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button onClick={() => setOpenIssue(lock)} className="!py-1.5 !px-3 text-xs" data-testid={`issue-pin-${lock.id}`}><Plus className="w-3.5 h-3.5"/> Issue PIN</Button>
                    <Button variant="ghost" onClick={() => openLockAudit(lock)} className="!py-1.5 !px-3 text-xs" data-testid={`audit-${lock.id}`}><Activity className="w-3.5 h-3.5"/> Audit</Button>
                  </div>
                </div>
                {codes.length > 0 && (
                  <div className="mt-4 -mx-2 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <tr><th className="px-2 py-1.5">PIN</th><th className="px-2 py-1.5">Granted to</th><th className="px-2 py-1.5">Role</th><th className="px-2 py-1.5">Uses</th><th className="px-2 py-1.5">Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {codes.map(c => (
                          <tr key={c.id} className="border-t border-slate-100" data-testid={`pin-row-${c.id}`}>
                            <td className="px-2 py-2 font-mono font-bold text-slate-900 inline-flex items-center gap-1.5">{c.code}
                              {c.status === "active" && <button onClick={() => copyCode(c.code)} className="text-slate-400 hover:text-slate-900" data-testid={`copy-pin-${c.id}`}><Copy className="w-3 h-3"/></button>}
                            </td>
                            <td className="px-2 py-2 text-slate-700">{c.granted_to_name}</td>
                            <td className="px-2 py-2 text-xs uppercase tracking-wide text-slate-500">{c.role}</td>
                            <td className="px-2 py-2 text-xs text-slate-600">{c.uses || 0}/{c.max_uses}</td>
                            <td className="px-2 py-2"><Pill tone={STATUS_TONE[c.status] || "slate"}>{c.status}</Pill></td>
                            <td className="px-2 py-2 text-right">
                              {c.status === "active" && (
                                <button onClick={() => revoke(c)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" title="Revoke" data-testid={`revoke-pin-${c.id}`}><Ban className="w-3.5 h-3.5"/></button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={openProvision} onClose={() => setOpenProvision(false)} title="Provision a smart lock" testid="provision-modal">
        <form onSubmit={provision} className="space-y-4">
          <Select label="Listing" required value={pform.listing_id} onChange={(e) => setPform({...pform, listing_id: e.target.value})} testid="provision-listing">
            <option value="">Select listing</option>
            {listings.map(l => <option key={l.id} value={l.id}>{l.title} — {l.city}, {l.province}</option>)}
          </Select>
          <Input label="Nickname" required placeholder="Front door — 2nd-floor unit" value={pform.nickname} onChange={(e) => setPform({...pform, nickname: e.target.value})}/>
          <Select label="Provider" value={pform.provider} onChange={(e) => setPform({...pform, provider: e.target.value})}>
            {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
          <Input label="Provider device id (optional)" placeholder="e.g. RemoteLock device serial" value={pform.provider_device_id} onChange={(e) => setPform({...pform, provider_device_id: e.target.value})}/>
          {pform.provider !== "stub" && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">⚠️ {PROVIDERS.find(p => p.value === pform.provider)?.label} hardware integration is wired-but-not-active — actual unlock calls will fail until we ship the SDK. Use the Stub provider to demo / test.</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenProvision(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="provision-submit">{busy ? "Provisioning…" : "Provision"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!openIssue} onClose={() => setOpenIssue(null)} title={`Issue PIN — ${openIssue?.nickname || ""}`} testid="issue-pin-modal">
        <form onSubmit={issueCode} className="space-y-4">
          <Input label="Granted to (name)" required value={iform.granted_to_name} onChange={(e) => setIform({...iform, granted_to_name: e.target.value})} testid="issue-name"/>
          <Input label="Granted to (email, optional)" type="email" value={iform.granted_to_email} onChange={(e) => setIform({...iform, granted_to_email: e.target.value})}/>
          <Select label="Role" value={iform.role} onChange={(e) => setIform({...iform, role: e.target.value})}>{ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}</Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Valid from" type="datetime-local" value={iform.valid_from} onChange={(e) => setIform({...iform, valid_from: e.target.value})}/>
            <Input label="Valid until" type="datetime-local" value={iform.valid_to} onChange={(e) => setIform({...iform, valid_to: e.target.value})}/>
          </div>
          <Input label="Max uses" type="number" min="1" max="500" value={iform.max_uses} onChange={(e) => setIform({...iform, max_uses: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenIssue(null)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="issue-submit">{busy ? "Issuing…" : "Issue PIN"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!openAudit} onClose={() => setOpenAudit(null)} title={`Audit log — ${openAudit?.lock?.nickname || ""}`} testid="audit-modal">
        {openAudit && (openAudit.items.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">No access events yet.</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {openAudit.items.map(ev => (
              <div key={ev.id} className="py-2.5 flex items-center justify-between text-sm" data-testid={`audit-row-${ev.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 flex items-center gap-1.5">
                    {ev.event === "unlocked" && <Lock className="w-3.5 h-3.5 text-emerald-700"/>}
                    {ev.event === "denied" && <Ban className="w-3.5 h-3.5 text-rose-600"/>}
                    {ev.event}
                  </div>
                  <div className="text-xs text-slate-500">{ev.actor || "—"} {ev.actor_role ? `· ${ev.actor_role}` : ""} {ev.ip ? `· ${ev.ip}` : ""}</div>
                  {ev.info && ev.info !== "ok" && <div className="text-xs text-rose-600">{ev.info}</div>}
                </div>
                <div className="text-xs text-slate-500">{new Date(ev.created_at).toLocaleString("en-CA", { dateStyle: "short", timeStyle: "short" })}</div>
              </div>
            ))}
          </div>
        ))}
      </Modal>
    </div>
  );
}
